import { isPwaEnabled } from '../../../utils/offline_gating';
import { recordPwaMetric } from '../../../pwa/performanceMetrics.js';
import { caseViewerDocumentId } from '../../../utils/case_experience/modal_pending_storage';

const ASSESSMENT_PENDING_NEXT_URL_KEY = 'assessmentOfflinePendingNextUrl';
const PENDING_SYNC_NOTIFICATION_KEY = 'offlineSyncNotificationMessage';
const CASE_CHROME_STREAM_TARGETS = [
  'case-experience-sticky-bar-desktop',
  'case-experience-sticky-bar-mobile',
  'case-experience-section-nav',
];

export class OfflineProgressSync {
  constructor() {
    this.dbName = 'AquiferOfflineSync';
    this.dbVersion = 3;
    this.storeName = 'progressQueue';
    this.cacheStoreName = 'documentUserStateCache';
    this.draftStoreName = 'quizItemResponseDrafts';
    this.db = null;
    this.isOnline = navigator.onLine;
    this.pendingCount = 0;
    this.initPromise = this.initDB();
    this.setupEventListeners();
    this.setupBeforeUnloadGuard();
    this.showPendingSyncNotification();
    this.bootstrapSync();
    this.pendingAssessmentResyncAttempts = 0;
    this.syncInFlight = false;
  }

  // Wait for the database the constructor already started opening.
  async ensureDB() {
    if (!this.db) await this.initPromise;
  }

  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = async () => {
        this.db = request.result;
        this.db.onversionchange = () => {
          this.db.close();
          this.db = null;
        };
        try {
          const existing = await this.getQueuedUpdates();
          this.pendingCount = existing.length;
        } catch (_error) {
          this.pendingCount = 0;
        }
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true
          });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('type', 'type', { unique: false });
        }

        if (!db.objectStoreNames.contains(this.cacheStoreName)) {
          db.createObjectStore(this.cacheStoreName, { keyPath: 'key' });
        }

        if (!db.objectStoreNames.contains(this.draftStoreName)) {
          db.createObjectStore(this.draftStoreName, { keyPath: 'key' });
        }
      };
    });
  }

  bootstrapSync() {
    this.initPromise
      ?.then(() => this.syncIfOnline())
      .catch((error) => console.error('Error bootstrapping offline sync:', error));
  }

  setupEventListeners() {
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('pageshow', () => this.syncIfOnline());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.syncIfOnline();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.showWaitingForConnection();
    });

    // Poll as a fallback in case the browser misses online/offline events
    if (!window.__aquiferOfflinePoll) {
      window.__aquiferOfflinePoll = setInterval(() => {
        const currentlyOnline = navigator.onLine;
        if (currentlyOnline !== this.isOnline) {
          this.isOnline = currentlyOnline;
          if (currentlyOnline) {
            this.handleOnline();
          } else {
            this.showWaitingForConnection();
          }
        }
      }, 2000);
    }
  }

  handleOnline() {
    this.isOnline = true;
    this.syncIfOnline();
  }

  syncIfOnline() {
    if (this.isOnline) {
      this.syncQueuedUpdates();
    } else {
      this.showWaitingForConnection();
    }
  }

  showWaitingForConnection() {
    if (this.pendingCount > 0) this.renderSyncState('pending', this.pendingCount);
  }

  renderSyncState(state, count, waitingCount) {
    const noun = document.getElementById('sync-status')?.dataset.syncNoun || 'answer';
    const plural = count === 1 ? noun : `${noun}s`;
    const answers = `${count} ${plural}`;
    const messages = {
      partial: `Synced ${answers}, ${waitingCount} waiting`,
      pending: `${answers} saved, waiting for connection`,
      syncing: `Syncing ${answers}…`,
      failed: `${answers} could not sync`,
      signInRequired: `Sign in to sync ${count} saved ${plural}`,
    };
    this.showSyncStatus();
    this.updateSyncMessage(messages[state]);
    ['sync-retry', 'sync-discard'].forEach((id) => {
      const control = document.getElementById(id);
      if (control) control.hidden = state !== 'failed';
    });
  }

  setupBeforeUnloadGuard() {
    if (window.__aquiferOfflineBeforeUnload) return;
    window.__aquiferOfflineBeforeUnload = (event) => this.handleBeforeUnload(event);
    window.addEventListener('beforeunload', window.__aquiferOfflineBeforeUnload);
  }

  allowNextUnload() {
    this.allowUnloadOnce = true;
  }

  get pendingVisit() {
    return window.__aquiferPendingVisit ?? null;
  }

  set pendingVisit(href) {
    window.__aquiferPendingVisit = href;
  }

  handleBeforeUnload(event) {
    if (this.allowUnloadOnce) {
      this.allowUnloadOnce = false;
      return;
    }
    if (!this.hasUnsyncedOfflineWork()) return;
    event.preventDefault();
    event.returnValue = '';
  }

  hasUnsyncedOfflineWork() {
    return !this.isOnline && this.pendingCount > 0;
  }

  /**
   * Queue a document user state update for later sync
   */
  async queueDocumentUserStateUpdate(
    documentId,
    userId,
    state,
    endpoint = 'api/v2/document_user_states',
    clientReset = 0,
  ) {
    await this.ensureDB();

    const updateData = {
      type: 'document_user_state',
      documentId: documentId,
      userId: userId,
      state: state,
      endpoint: endpoint,
      clientReset: clientReset,
      timestamp: Date.now(),
    };

    const result = await this.addToQueue(updateData);
    this.showSyncStatus();
    this.updateSyncMessage('Progress saved locally – will sync when online');
    return result;
  }

  /**
   * Queue a document user log entry for later sync
   */
  async queueDocumentUserLog(documentId, logType, args = {}) {
    await this.ensureDB();

    const logData = {
      type: 'document_user_log',
      documentId: documentId,
      logType: logType,
      args: args,
      timestamp: Date.now(),
    };

    const result = await this.addToQueue(logData);
    this.showSyncStatus();
    this.updateSyncMessage('Progress saved locally – will sync when online');
    return result;
  }

  /**
   * Queue a quiz item response for later sync
   */
  async queueQuizItemResponse({ quizId, questionIndex, mode, formData, correct }) {
    await this.ensureDB();

    const updateData = {
      type: 'quiz_item_response',
      quizId,
      questionIndex,
      mode,
      formData,
      correct,
      endpoint: `/quiz_item_responses/${questionIndex}`,
      timestamp: Date.now(),
    };

    const result = await this.replaceInQueue(updateData, (queued) => this.answersQuestion(queued, quizId, questionIndex));
    this.renderSyncState('pending', this.pendingCount);
    return result;
  }

  async removeQueuedQuizItemResponse(quizId, questionIndex) {
    const queued = await this.getQueuedUpdates();
    for (const update of queued.filter((queuedUpdate) => this.answersQuestion(queuedUpdate, quizId, questionIndex))) {
      await this.removeFromQueue(update.id);
    }
  }

  answersQuestion(queued, quizId, questionIndex) {
    return (
      queued.type === 'quiz_item_response' &&
      String(queued.quizId) === String(quizId) &&
      String(queued.questionIndex) === String(questionIndex)
    );
  }

  answersAssessmentItem(queued, rosteringId, itemIndex) {
    return (
      queued.type === 'assessment_item_response' &&
      Number(queued.rosteringId) === Number(rosteringId) &&
      Number(queued.itemIndex) === Number(itemIndex) &&
      this.ownedBySignedInUser(queued)
    );
  }

  async pendingAssessmentItemResponse(rosteringId, itemIndex) {
    const queued = await this.getQueuedUpdates();
    return queued.filter(
      (update) =>
        this.answersAssessmentItem(update, rosteringId, itemIndex) &&
        update.status !== 'failed',
    );
  }

  assessmentSyncMatchesPathname(syncedItem, pathname) {
    if (syncedItem.currentPath && syncedItem.currentPath === pathname) return true;

    const match = pathname.match(/\/assessment\/rosterings\/(\d+)\/item_responses\/(\d+)/);
    if (!match) return false;
    if (syncedItem.rosteringId == null || syncedItem.itemIndex == null) {
      return Boolean(syncedItem.legacyRedirect);
    }

    return (
      Number(syncedItem.rosteringId) === Number(match[1]) &&
      Number(syncedItem.itemIndex) === Number(match[2])
    );
  }

  /**
   * Block Calibrate offline Continue until this question's queued answer has synced.
   */
  async waitForAssessmentItemResponseSynced(rosteringId, itemIndex, { maxWaitMs = 60_000 } = {}) {
    await this.ensureDB();
    const deadline = Date.now() + maxWaitMs;

    while (Date.now() < deadline) {
      const queued = await this.signedInUserQueue();
      const failed = queued.filter(
        (update) =>
          this.answersAssessmentItem(update, rosteringId, itemIndex) &&
          update.status === 'failed',
      );
      if (failed.length > 0) {
        return {
          ok: false,
          message: 'Unable to save your answer. Please try again.',
          failed: true,
        };
      }

      const pending = queued.filter(
        (update) =>
          this.answersAssessmentItem(update, rosteringId, itemIndex) &&
          update.status !== 'failed',
      );
      if (pending.length === 0) return { ok: true };

      if (this.isOnline && !this.syncInFlight) {
        await this.syncQueuedUpdates();
      }

      await new Promise((resolve) => setTimeout(resolve, 150));
    }

    return {
      ok: false,
      message: 'Still saving your answer. Please try again.',
      transient: true,
    };
  }

  /**
   * Queue an assessment item response for later sync.
   */
  async queueAssessmentItemResponse({ rosteringId, itemIndex, mode, formData, nextQuestionUrl, currentPath }) {
    await this.ensureDB();

    const updateData = {
      type: 'assessment_item_response',
      rosteringId,
      itemIndex,
      mode,
      formData,
      nextQuestionUrl,
      currentPath,
      endpoint: `/assessment/rosterings/${rosteringId}/item_responses/${itemIndex}`,
      timestamp: Date.now(),
    };

    const result = await this.addToQueue(updateData);
    this.showSyncStatus();
    this.updateSyncMessage('Progress saved locally - will sync when online');
    return result;
  }

  // Queue a Continue so the server can apply it once the student is back online.
  async queueCaseExperienceUnlock({ documentId, userId, currentSectionUid, rootUids, adminPreviewMode }) {
    await this.ensureDB();

    

    const updateData = {
      type: 'case_experience_unlock',
      documentId,
      userId,
      currentSectionUid,
      adminPreviewMode,
      rootUids: rootUids || [],
      endpoint: `/documents/${documentId}/case_experience/unlock_next_section`,
      timestamp: Date.now(),
      retryCount: 0,

    };

    const result = await this.addToQueue(updateData);
    this.showSyncStatus();
    this.updateSyncMessage('Progress saved locally – will sync when online');
    if (this.isOnline) void this.syncQueuedUpdates();
    return result;
  }

  // Remember which section is open, keeping progress already stored for this case.
  async rememberCaseExperienceActiveSection(documentId, userId, { state, nextUid } = {}) {
    const existing = await this.readCachedDocumentUserState(documentId, userId);
    const payload = {
      ...(existing && typeof existing === 'object' ? existing : {}),
      ...(state && typeof state === 'object' ? { state } : {}),
    };
    if (nextUid) payload.offlineActiveSectionUid = nextUid;
    return this.cacheDocumentUserState(documentId, userId, payload);
  }

  async cacheDocumentUserState(documentId, userId, payload) {
    if (!documentId || !userId) return;
    await this.ensureDB();
    if (!this.db.objectStoreNames.contains(this.cacheStoreName)) return;

    const record = {
      key: this.documentUserStateCacheKey(documentId, userId),
      documentId,
      userId,
      payload: await this.payloadKeepingOfflineSection(documentId, userId, payload),
      updatedAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.cacheStoreName], 'readwrite');
      const store = transaction.objectStore(this.cacheStoreName);
      const request = store.put(record);
      request.onsuccess = () => resolve(record);
      request.onerror = () => reject(request.error);
    });
  }

  async clearDocumentUserStateProgress(documentId, userId) {
    if (!documentId || !userId) return;
    await this.ensureDB();
    await this.clearDocumentUserStateCache(documentId, userId);
    await this.clearQueuedDocumentUserStateUpdates(documentId, userId);
  }

  async clearDocumentUserStateCache(documentId, userId) {
    if (!this.db?.objectStoreNames.contains(this.cacheStoreName)) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.cacheStoreName], 'readwrite');
      const store = transaction.objectStore(this.cacheStoreName);
      const request = store.delete(this.documentUserStateCacheKey(documentId, userId));
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearQueuedDocumentUserStateUpdates(documentId, userId) {
    if (!this.db?.objectStoreNames.contains(this.storeName)) return;

    const queuedUpdates = await this.getQueuedUpdates();
    const staleUpdates = queuedUpdates.filter(
      (update) =>
        (update.type === 'document_user_state' || update.type === 'case_experience_unlock') &&
        String(update.documentId) === String(documentId) &&
        String(update.userId) === String(userId),
    );

    for (const update of staleUpdates) {
      await this.removeFromQueue(update.id);
    }
  }

  // A newer cache write should not forget the section the student had open offline.
  async payloadKeepingOfflineSection(documentId, userId, payload) {
    if (!payload || typeof payload !== 'object' || Object.prototype.hasOwnProperty.call(payload, 'offlineActiveSectionUid')) {
      return payload;
    }

    const existing = await this.readCachedDocumentUserState(documentId, userId);
    if (!existing || typeof existing !== 'object' || !existing.offlineActiveSectionUid) return payload;
    return { ...payload, offlineActiveSectionUid: existing.offlineActiveSectionUid };
  }

  async readCachedDocumentUserState(documentId, userId) {
    if (!documentId || !userId) return null;
    await this.ensureDB();
    if (!this.db.objectStoreNames.contains(this.cacheStoreName)) return null;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.cacheStoreName], 'readonly');
      const store = transaction.objectStore(this.cacheStoreName);
      const request = store.get(this.documentUserStateCacheKey(documentId, userId));
      request.onsuccess = () => resolve(request.result?.payload || null);
      request.onerror = () => reject(request.error);
    });
  }

  async saveQuizItemResponseDraft(quizId, questionIndex, formData) {
    await this.storeRequest(this.draftStoreName, 'readwrite', (store) =>
      store.put({ key: this.quizItemResponseDraftKey(quizId, questionIndex), formData }),
    );
  }

  async readQuizItemResponseDraft(quizId, questionIndex) {
    const draft = await this.storeRequest(this.draftStoreName, 'readonly', (store) =>
      store.get(this.quizItemResponseDraftKey(quizId, questionIndex)),
    );
    return draft?.formData ?? null;
  }

  async clearQuizItemResponseDraft(quizId, questionIndex) {
    await this.storeRequest(this.draftStoreName, 'readwrite', (store) =>
      store.delete(this.quizItemResponseDraftKey(quizId, questionIndex)),
    );
  }

  async storeRequest(storeName, mode, operation) {
    await this.ensureDB();

    return new Promise((resolve, reject) => {
      const request = operation(this.db.transaction([storeName], mode).objectStore(storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  quizItemResponseDraftKey(quizId, questionIndex) {
    return `quiz-${quizId}:question-${questionIndex}`;
  }

  documentUserStateCacheKey(documentId, userId) {
    return `document-${documentId}:user-${userId}`;
  }

  async addToQueue(data) {
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.add(this.withOwner(data));
      let insertedId;
      request.onsuccess = () => { insertedId = request.result; };
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => {
        this.pendingCount = (this.pendingCount || 0) + 1;
        this.recordQueuedMetric(data.type);
        resolve(insertedId);
      };
      transaction.onabort = () => reject(transaction.error || request.error);
    });
  }

  async replaceInQueue(data, isSameItem) {
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const existing = store.getAll();
      let removedCount = 0;
      let insertedId;
      existing.onsuccess = () => {
        existing.result.filter(isSameItem).forEach((queued) => {
          store.delete(queued.id);
          removedCount += 1;
        });
        const added = store.add(this.withOwner(data));
        added.onsuccess = () => { insertedId = added.result; };
      };
      existing.onerror = () => reject(existing.error);
      transaction.oncomplete = () => {
        this.pendingCount = Math.max(0, (this.pendingCount || 0) - removedCount) + 1;
        this.recordQueuedMetric(data.type);
        resolve(insertedId);
      };
      transaction.onabort = () => reject(transaction.error);
    });
  }

  withOwner(data) {
    return { userId: document.body.dataset.userId, ...data };
  }

  ownedBySignedInUser(update) {
    const signedInUserId = document.body.dataset.userId;
    return Boolean(signedInUserId) && String(update.userId) === signedInUserId;
  }

  recordQueuedMetric(type) {
    if (this.isOnline) return;
    recordPwaMetric('offline_sync', 'offline_hit_queued', {
      metricName: 'queue_depth',
      value: this.pendingCount,
      unit: 'count',
      online: false,
      metadata: { type }
    });
  }

  /**
   * Get all queued updates
   */
  async getQueuedUpdates() {
    await this.ensureDB();

    const transaction = this.db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove item from queue after successful sync
   */
  async removeFromQueue(id) {
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => {
        this.pendingCount = Math.max(0, (this.pendingCount || 0) - 1);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async updateQueueItem(update) {
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const existing = store.get(update.id);
      existing.onsuccess = () => {
        if (!existing.result) return resolve();
        const request = store.put(update);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      };
      existing.onerror = () => reject(existing.error);
    });
  }

  /**
   * Sync all queued updates when back online
   */
  async syncQueuedUpdates() {
    if (this.syncInFlight) {
      this.syncAgain = true;
      return;
    }
    this.syncInFlight = true;
    try {
      if (!this.isOnline) return;
      await this.ensureDB();

      const allQueued = await this.signedInUserQueue();
      const queuedUpdates = allQueued.filter((update) => update.status !== 'failed');
      let failedCount = allQueued.length - queuedUpdates.length;
      if (queuedUpdates.length === 0) {
        if (failedCount > 0) {
          this.renderSyncState('failed', failedCount);
        } else {
          this.hideSyncStatus();
        }
        return;
      }
      this.renderSyncState('syncing', queuedUpdates.length);
      console.log(`Syncing ${queuedUpdates.length} queued progress updates...`);

      let syncedQuizResponses = 0;
      let syncedAssessmentResponses = 0;
      let syncedAssessmentItems = [];
      let syncedDocumentUserStates = 0;
      let syncedCount = 0;
      // A failed Continue stays queued. Later Continues for that case wait until it is retried.
      const heldCaseUnlocks = new Set(
        allQueued
          .filter((update) => update.type === 'case_experience_unlock' && update.status === 'failed')
          .map((update) => `${update.documentId}:${update.userId}`),
      );
      let halted = false;
      let signInRequired = false;

      for (const update of queuedUpdates) {
        const unlockKey = update.type === 'case_experience_unlock'
          ? `${update.documentId}:${update.userId}`
          : null;
        if (unlockKey && heldCaseUnlocks.has(unlockKey)) continue;

        try {
          const contextLabel = update.type === 'quiz_item_response'
            ? `quiz ${update.quizId} question ${update.questionIndex}`
            : update.type === 'assessment_item_response'
              ? `assessment rostering ${update.rosteringId} item ${update.itemIndex}`
              : update.type === 'case_experience_unlock'
                ? `case ${update.documentId} from ${update.currentSectionUid}`
                : `document ${update.documentId}`;
          console.log(`Attempting to sync ${update.type} for ${contextLabel}...`);
          await this.syncSingleUpdate(update);
          if (update.type === 'quiz_item_response') {
            syncedQuizResponses += 1;
          } else if (update.type === 'assessment_item_response') {
            syncedAssessmentResponses += 1;
            syncedAssessmentItems.push({
              rosteringId: update.rosteringId,
              itemIndex: update.itemIndex,
              currentPath: update.currentPath,
              legacyRedirect:
                update.rosteringId == null && update.itemIndex == null,
            });
          } else if (update.type === 'document_user_state') {
            syncedDocumentUserStates += 1;
          }
          syncedCount += 1;
          if (update.timestamp) {
            recordPwaMetric('offline_sync', 'offline_sync_completed', {
              metricName: 'sync_duration',
              value: Date.now() - update.timestamp,
              unit: 'ms',
              metadata: { type: update.type }
            });
          }
          await this.removeFromQueue(update.id);
          console.log(`✅ Successfully synced ${update.type} for ${contextLabel}`);
        } catch (error) {
          console.error(`❌ Failed to sync update ${update.id}:`, error);
          if (error.hold && unlockKey) {
            heldCaseUnlocks.add(unlockKey);
            continue;
          }
          const kind = this.failureKind(error);
          if (kind === 'transient') {
            halted = true;
            this.renderSyncState('pending', queuedUpdates.length - syncedCount);
            this.scheduleRetry();
            break;
          }
          if (kind === 'auth') {
            signInRequired = true;
            break;
          }
          if (unlockKey) heldCaseUnlocks.add(unlockKey);
          recordPwaMetric('offline_sync', 'offline_sync_failed', {
            metricName: 'failure_count',
            value: 1,
            unit: 'count',
            httpStatus: error.status,
            metadata: { type: update.type }
          });
          await this.updateQueueItem({ ...update, status: 'failed' });
          failedCount += 1;
        }
      }

      if (!halted) this.retryAttempt = 0;

      // Show user notification only for successfully synced items
      if (syncedCount > 0) {
        recordPwaMetric('offline_sync', 'offline_hits_flushed', {
          metricName: 'success_count',
          value: syncedCount,
          unit: 'count'
        });
        const triggersNavigation = syncedQuizResponses > 0 || syncedAssessmentResponses > 0 || syncedDocumentUserStates > 0;
        this.showSyncNotification(syncedCount, { persistAcrossNavigation: triggersNavigation });
        this.hideSyncStatus();
      }

      if (signInRequired) {
        this.renderSyncState('signInRequired', allQueued.length - syncedCount);
        return;
      }

      const stillWaiting = (await this.getQueuedUpdates()).filter((queued) => queued.status !== 'failed');
      if (syncedCount > 0 && stillWaiting.length > 0) {
        this.renderSyncState('partial', syncedCount, stillWaiting.length);
      }

      if (failedCount > 0) {
        this.renderSyncState('failed', failedCount);
      } else if (syncedCount === 0 && stillWaiting.length > 0) {
        this.renderSyncState('pending', stillWaiting.length);
      }

      const currentQuizId = new URLSearchParams(window.location.search).get('quiz_id');
      const currentQuizWaiting = stillWaiting.some(
        (queued) => queued.type === 'quiz_item_response' && String(queued.quizId) === currentQuizId,
      );
      if (syncedQuizResponses > 0 && window.location.pathname.includes('/quiz_item_responses/') && !currentQuizWaiting && !this.pendingVisit && !window.__aquiferVisiting) {
        setTimeout(() => window.location.reload(), 300);
        return;
      }

      if (syncedAssessmentResponses > 0 && window.location.pathname.includes('/assessment/rosterings/')) {
        const pathname = window.location.pathname;
        const redirectCurrentItem = syncedAssessmentItems.some((item) =>
          this.assessmentSyncMatchesPathname(item, pathname),
        );
        if (redirectCurrentItem) {
          this.clearPendingAssessmentNextUrl();
          const onEdit = pathname.endsWith('/edit');
          const explanationPath = `${pathname.replace(/\/edit$/, '')}${window.location.search}`;
          setTimeout(() => {
            if (onEdit) {
              window.location.assign(explanationPath);
            } else {
              window.location.reload();
            }
          }, 300);
        }
        return;
      }

      if (syncedDocumentUserStates > 0 && window.location.pathname.includes('/document_set_document_relations/')) {
        setTimeout(() => window.location.reload(), 300);
        return;
      }

    } catch (error) {
      console.error('Error syncing queued updates:', error);
    } finally {
      this.syncInFlight = false;
      if (this.syncAgain) {
        this.syncAgain = false;
        void this.syncQueuedUpdates();
      }
    }
  }

  async discardUnownedData() {
    const queued = await this.getQueuedUpdates();
    const disposable = queued.filter(
      (queuedUpdate) => queuedUpdate.type !== 'quiz_item_response' || !queuedUpdate.userId,
    );
    for (const update of disposable) {
      await this.removeFromQueue(update.id);
    }
    await Promise.all([this.cacheStoreName, this.draftStoreName].map((storeName) => this.clearStore(storeName)));
  }

  clearStore(storeName) {
    return this.storeRequest(storeName, 'readwrite', (store) => store.clear());
  }

  async signedInUserQueue() {
    if (!document.body.dataset.userId) return [];

    const queued = await this.getQueuedUpdates();
    for (const update of queued.filter((queuedUpdate) => !this.ownedBySignedInUser(queuedUpdate))) {
      await this.removeFromQueue(update.id);
    }
    return queued.filter((update) => this.ownedBySignedInUser(update));
  }

  async pendingFor(quizId) {
    const queued = await this.getQueuedUpdates();
    return queued.filter(
      (update) =>
        update.type === 'quiz_item_response' &&
        String(update.quizId) === String(quizId) &&
        this.ownedBySignedInUser(update),
    );
  }

  async retryFailed() {
    const queued = await this.getQueuedUpdates();
    for (const update of queued.filter((queuedUpdate) => queuedUpdate.status === 'failed')) {
      await this.updateQueueItem({ ...update, status: 'pending' });
    }
    return this.syncQueuedUpdates();
  }

  async discardFailed() {
    const queued = await this.getQueuedUpdates();
    for (const update of queued.filter((queuedUpdate) => queuedUpdate.status === 'failed')) {
      await this.removeFromQueue(update.id);
    }
    const remaining = await this.getQueuedUpdates();
    if (remaining.length === 0) {
      this.hideSyncStatus();
    } else {
      this.renderSyncState('pending', remaining.length);
    }
  }

  scheduleRetry() {
    const delays = [5000, 15000, 60000];
    const attempt = this.retryAttempt || 0;
    this.retryAttempt = attempt + 1;
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.syncQueuedUpdates(), delays[Math.min(attempt, delays.length - 1)]);
  }

  failureKind(error) {
    if (error.name === 'TypeError' || error.name === 'AbortError') return 'transient';
    if (error.status === 401) return 'auth';
    if (error.status === 408 || error.status === 429 || error.status >= 500) return 'transient';
    return 'permanent';
  }

  async syncSingleUpdate(update) {
    if (update.type === 'document_user_state') {
      return this.syncDocumentUserState(update);
    } else if (update.type === 'case_experience_unlock') {
      return this.syncCaseExperienceUnlock(update);
    } else if (update.type === 'document_user_log') {
      return this.syncDocumentUserLog(update);
    } else if (update.type === 'quiz_item_response') {
      return this.syncQuizItemResponse(update);
    } else if (update.type === 'assessment_item_response') {
      return this.syncAssessmentItemResponse(update);
    }
  }

  async syncDocumentUserState(update) {
    // Build correct URL format: /api/v2/document_user_states/:document_id/:user_id
    const endpoint = update.endpoint.replace(/\/$/, '');
    const url = `/${endpoint}/${update.documentId}/${update.userId}`;

    // Debug logging to verify URL construction
    console.log(`🔧 Sync URL: ${url} (method: PUT)`);
    console.log(`🔧 Document ID: ${update.documentId}, User ID: ${update.userId}`);

    // Use ReactOnRails authentication headers like the rest of the app
    const headers = window.ReactOnRails ?
      window.ReactOnRails.authenticityHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }) : {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      };

    const response = await fetch(url, {
      method: 'PUT', // Use PUT as defined in routes
      headers: headers,
      body: JSON.stringify({
        document_user_state: {
          state: update.state,
          client_reset: update.clientReset ?? 0,
        }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw Object.assign(new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`), { status: response.status });
    }

    const data = await response.json();

    // Update progress display with synced data
    if (window.updateProgress && typeof window.updateProgress === 'function') {
      window.updateProgress(data);
    }

    return data;
  }

  // Replay a queued Continue against the unlock endpoint.
  async syncCaseExperienceUnlock(update) {
    const url = update.endpoint || `/documents/${update.documentId}/case_experience/unlock_next_section`;
    const csrfToken = await this.fetchFreshCsrfToken(url);
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        current_section_uid: update.currentSectionUid,
        ...(update.adminPreviewMode ? { admin_preview_mode: update.adminPreviewMode } : {}),
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      let body = {};
      try {
        body = JSON.parse(errorText);
      } catch (_error) {
        body = {};
      }
      const errorCode = body.error_code ?? null;
      // ponytail: a row with no rootUids cannot tell "already past" from "still here", so it holds.
      if (response.status === 422 && errorCode === 'not_active_section') {
        if (this.unlockAlreadyApplied(update, body.active_section_uid)) return {};
        if (!update.collapseRetried && await this.syncCollapseForUnlock(update)) {
          return this.syncCaseExperienceUnlock({ ...update, collapseRetried: true });
        }
        const held = new Error('not_active_section');
        held.hold = true;
        throw held;
      }

      throw Object.assign(
        new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`),
        { status: response.status },
      );
    }

    const data = await response.json().catch(() => ({}));
    if (await this.laterCaseUnlockQueued(update)) return data;

    if (data.turbo_streams && caseViewerDocumentId() === Number(update.documentId)) {
      window.Turbo?.renderStreamMessage(this.caseChromeStreams(data.turbo_streams));
    }

    try {
      await this.rememberCaseExperienceActiveSection(update.documentId, update.userId, {
        state: data.state,
        nextUid: data.next_revealed_section_uid,
      });
    } catch (error) {
      console.warn('[CaseOffline] failed to store synced case progress', error);
    }
    return data;
  }

  unlockAlreadyApplied(update, activeUid) {
    const order = update.rootUids || [];
    const from = order.indexOf(update.currentSectionUid);
    const active = order.indexOf(activeUid);
    return from >= 0 && active > from;
  }

  async syncCollapseForUnlock(update) {
    const rootUids = update.rootUids || [];
    if (!rootUids.length || !update.documentId) return false;

    const container = document.getElementById('document-container');
    const raw =
      container?.getAttribute('data-case-viewer-document-user-state-reset-value') ||
      container?.getAttribute('data-case-experience--case-viewer-document-user-state-reset-value');
    const clientReset = parseInt(raw, 10);
    if (!container || Number.isNaN(clientReset)) return false;

    const updatedAt = Date.now();
    const state = {};
    for (const uid of rootUids) {
      state[uid] = { collapsed: { value: uid !== update.currentSectionUid, updatedAt } };
    }

    const url = `/documents/${update.documentId}/case_experience/patch_state`;
    const csrfToken = await this.fetchFreshCsrfToken(url);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        document_user_state: {
          state,
          client_reset: clientReset,
        },
      }),
    });
    return response.ok;
  }

  caseChromeStreams(turboStreams) {
    const template = document.createElement('template');
    template.innerHTML = turboStreams;
    return [...template.content.querySelectorAll('turbo-stream')]
      .filter((stream) => CASE_CHROME_STREAM_TARGETS.includes(stream.getAttribute('target')))
      .map((stream) => stream.outerHTML)
      .join('');
  }

  // A later Continue for this case is still waiting, so this sync should not move the open section.
  async laterCaseUnlockQueued(update) {
    try {
      const queued = await this.getQueuedUpdates();
      return queued.some((row) =>
        row.type === 'case_experience_unlock' &&
        row.id !== update.id &&
        String(row.documentId) === String(update.documentId) &&
        String(row.userId) === String(update.userId)
      );
    } catch (_error) {
      return true;
    }
  }

  async syncDocumentUserLog(update) {
    const url = `/documents/${update.documentId}/document_user_logs`;

    // Use ReactOnRails authentication headers like the rest of the app
    const headers = window.ReactOnRails ?
      window.ReactOnRails.authenticityHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }) : {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      };

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        document_user_log: {
          type: update.logType,
          created_at: Math.floor(update.timestamp / 1000),
          ...update.args
        }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw Object.assign(new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`), { status: response.status });
    }

    const data = await response.json();

    // Update progress display with synced data
    if (window.updateProgress && typeof window.updateProgress === 'function') {
      window.updateProgress(data);
    }

    return data;
  }

  async syncQuizItemResponse(update) {
    const params = new URLSearchParams();
    if (update.quizId) params.set('quiz_id', update.quizId);
    if (update.mode) params.set('mode', update.mode);

    const url = `${update.endpoint}${params.toString() ? `?${params.toString()}` : ''}`;
    console.debug('[OfflineSync] quiz_item_response URL', url, {
      quizId: update.quizId,
      questionIndex: update.questionIndex,
      mode: update.mode,
      formData: update.formData
    });

    const headers = window.ReactOnRails ?
      window.ReactOnRails.authenticityHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
      }) : {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      };

    const body = new URLSearchParams();
    Object.entries(update.formData || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => body.append(key, v));
      } else {
        body.append(key, value);
      }
    });
    console.debug('[OfflineSync] quiz_item_response body', body.toString());

    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 15000);
    let response;
    try {
      response = await fetch(url, {
        method: 'PATCH',
        headers: headers,
        credentials: 'include',
        body: body.toString(),
        redirect: 'follow',
        signal: timeout.signal
      });
    } finally {
      clearTimeout(timer);
    }

    this.rejectSignInRedirect(response, 'Quiz');

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('[OfflineSync] quiz_item_response failed', {
        status: response.status,
        statusText: response.statusText,
        url,
        body: body.toString(),
        responseBodyPreview: errorText?.slice?.(0, 500)
      });
      const err = new Error(`Quiz sync failed: ${response.status} ${response.statusText} - ${errorText}`);
      err.status = response.status;
      throw err;
    }

    return response;
  }

  async syncAssessmentItemResponse(update) {
    let csrfToken = await this.fetchFreshCsrfToken(update.nextQuestionUrl || '/');
    try {
      return await this.patchAssessmentItemResponse(update, csrfToken);
    } catch (error) {
      if (error?.status === 404) {
        error.staleQueueItem = true;
        throw error;
      }

      // Session/CSRF can expire while offline. Refresh once and retry.
      // 403 after a successful prior write is handled server-side as 204 (idempotent).
      if (error?.status === 422) {
        csrfToken = await this.fetchFreshCsrfToken('/');
        try {
          return await this.patchAssessmentItemResponse(update, csrfToken);
        } catch (retryError) {
          if (retryError?.status === 404) {
            retryError.staleQueueItem = true;
          }
          throw retryError;
        }
      }

      throw error;
    }
  }

  currentFrozenExamSeconds(rosteringId) {
    try {
      const data = JSON.parse(localStorage.getItem('assessmentOfflineFrozenTimer') || 'null');
      if (!data || Number(data.rosteringId) !== Number(rosteringId)) return null;

      const savedAt = Number(data.savedAt) || Date.now();
      const elapsed = data.frozen || !navigator.onLine ? 0 : Math.max(0, (Date.now() - savedAt) / 1000);
      const remaining = Math.floor(Number(data.remainingSeconds) - elapsed);
      return remaining >= 0 ? remaining : null;
    } catch (_error) {
      return null;
    }
  }

  async creditFrozenExamTime(rosteringId) {
    const seconds = this.currentFrozenExamSeconds(rosteringId);
    if (seconds == null) return;

    try {
      const queued = await this.getQueuedUpdates();
      const pendingAnswer = queued.some((update) => (
        update.type === 'assessment_item_response' &&
        Number(update.rosteringId) === Number(rosteringId)
      ));
      if (pendingAnswer) return;

      const path = window.location.pathname.replace(/\/edit$/, '');
      const csrfToken = await this.fetchFreshCsrfToken(path);
      await this.patchAssessmentItemResponse({
        rosteringId,
        currentPath: path,
        endpoint: path,
        formData: { offline_remaining_seconds: seconds },
      }, csrfToken);
    } catch (error) {
      console.warn('[OfflineSync] Could not credit frozen exam time', error);
    }
  }

  buildUrlEncodedBody(formData, csrfToken) {
    const body = new URLSearchParams();
    Object.entries(formData || {}).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => body.append(key, v));
      } else if (value !== undefined && value !== null) {
        body.append(key, value);
      }
    });

    body.delete('authenticity_token');
    if (csrfToken) {
      body.set('authenticity_token', csrfToken);
    }
    return body;
  }

  extractCsrfTokenFromHtml(html) {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    } catch (error) {
      return '';
    }
  }

  async fetchFreshCsrfToken(preferredUrl) {
    if (!navigator.onLine) return '';
    const candidates = ['/', preferredUrl].filter(Boolean);

    for (const candidate of candidates) {
      try {
        const response = await fetch(candidate, {
          method: 'GET',
          headers: {
            'X-Offline-Sync': 'true'
          },
          credentials: 'include',
          cache: 'reload',
        });
        if (!response.ok) continue;
        const html = await response.text();
        const token = this.extractCsrfTokenFromHtml(html);
        if (token) return token;
      } catch (error) {
        // Try next candidate.
      }
    }

    return '';
  }

  async patchAssessmentItemResponse(update, csrfToken) {
    const params = new URLSearchParams();
    if (update.mode) params.set('mode', update.mode);
    const endpoint = this.resolveAssessmentEndpoint(update);
    const url = `${endpoint}${params.toString() ? `?${params.toString()}` : ''}`;

    const headers = window.ReactOnRails ?
      window.ReactOnRails.authenticityHeaders({
        'Accept': 'text/html,application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
      }) : {
        'Accept': 'text/html,application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      };

    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    headers['X-Offline-Sync'] = 'true';

    const formData = { ...(update.formData || {}) };
    const offlineRemainingSeconds = this.currentFrozenExamSeconds(update.rosteringId);
    if (offlineRemainingSeconds != null && formData.offline_remaining_seconds == null) {
      formData.offline_remaining_seconds = offlineRemainingSeconds;
    }
    const body = this.buildUrlEncodedBody(formData, csrfToken);
    body.set('offline_seconds', Math.max(0, Math.round((Date.now() - update.timestamp) / 1000)));

    const response = await fetch(url, {
      method: 'PATCH',
      headers: headers,
      credentials: 'include',
      body: body.toString(),
      redirect: 'follow'
    });

    this.rejectSignInRedirect(response, 'Assessment');

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('[OfflineSync] assessment_item_response failed', {
        status: response.status,
        statusText: response.statusText,
        url,
        endpoint,
        update,
        body: body.toString(),
        responseBodyPreview: errorText?.slice?.(0, 500),
      });
      const err = new Error(`Assessment sync failed: ${response.status} ${response.statusText} - ${errorText}`);
      err.status = response.status;
      throw err;
    }

    return response;
  }

  rejectSignInRedirect(response, label) {
    // Treat auth redirects as failed syncs, otherwise queued items can be lost silently.
    if (response.redirected && /\/users\/sign_in(?:\?|$)/.test(response.url || '')) {
      const err = new Error(`${label} sync failed: redirected to sign in (${response.url})`);
      err.status = 401;
      throw err;
    }
  }

  resolveAssessmentEndpoint(update) {
    const fallback = update.endpoint;
    const currentPath = update.currentPath;
    if (!currentPath || typeof currentPath !== 'string') return fallback;

    const match = currentPath.match(/^\/assessment\/rosterings\/\d+\/item_responses\/\d+(?:\/edit)?$/);
    if (!match) return fallback;
    return currentPath.replace(/\/edit$/, '');
  }

  showSyncNotification(count, { persistAcrossNavigation = false } = {}) {
    const message = this.syncNotificationMessage(count);
    if (persistAcrossNavigation) {
      this.persistSyncNotification(message);
    }
    this.renderSyncNotification(message);
  }

  syncNotificationMessage(count) {
    return `↻ Synced ${count} progress update${count > 1 ? 's' : ''}`;
  }

  persistSyncNotification(message) {
    try {
      sessionStorage.setItem(PENDING_SYNC_NOTIFICATION_KEY, message);
    } catch (_error) {
      // Ignore storage access errors.
    }
  }

  showPendingSyncNotification() {
    let message = '';
    try {
      message = sessionStorage.getItem(PENDING_SYNC_NOTIFICATION_KEY) || '';
      if (message) {
        sessionStorage.removeItem(PENDING_SYNC_NOTIFICATION_KEY);
      }
    } catch (_error) {
      message = '';
    }

    if (message) {
      this.renderSyncNotificationWhenReady(message);
    }
  }

  renderSyncNotificationWhenReady(message) {
    if (document.body) {
      this.renderSyncNotification(message);
    } else {
      document.addEventListener('DOMContentLoaded', () => this.renderSyncNotification(message), { once: true });
    }
  }

  renderSyncNotification(message) {
    if (!message || !document.body) return;

    const existing = document.getElementById('offline-sync-toast');
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    const toast = document.createElement('div');
    toast.id = 'offline-sync-toast';
    toast.style.cssText = `
      position: fixed;
      top: 72px;
      right: 20px;
      background: linear-gradient(90deg, #002362, #1b5599);
      color: white;
      padding: 10px 16px;
      border-radius: 4px;
      z-index: 10001;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 2px 10px rgba(0, 35, 98, 0.25);
      pointer-events: none;
      max-width: 360px;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 5000);
  }

  getPendingAssessmentNextUrl() {
    try {
      const candidate = localStorage.getItem(ASSESSMENT_PENDING_NEXT_URL_KEY) || '';
      if (!candidate.startsWith('/') || candidate.startsWith('//')) return '';
      return candidate;
    } catch (_error) {
      return '';
    }
  }

  clearPendingAssessmentNextUrl() {
    try {
      localStorage.removeItem(ASSESSMENT_PENDING_NEXT_URL_KEY);
    } catch (_error) {
      // Ignore storage access errors.
    }
  }

  /**
   * Check if we should queue updates (offline) or send immediately (online)
   */
  shouldQueue() {
    return isPwaEnabled() && !this.isOnline;
  }

  /**
   * Get queue status for debugging
   */
  async getQueueStatus() {
    const updates = await this.getQueuedUpdates();
    return {
      isOnline: this.isOnline,
      queuedCount: updates.length,
      updates: updates
    };
  }

  /**
   * Show sync status indicator
   */
  showSyncStatus() {
    const syncStatus = document.getElementById('sync-status');
    if (syncStatus) {
      syncStatus.classList.remove('hidden');
    }
  }

  /**
   * Hide sync status indicator
   */
  hideSyncStatus() {
    const syncStatus = document.getElementById('sync-status');
    if (syncStatus) {
      syncStatus.classList.add('hidden');
    }
  }

  /**
   * Update sync status message
   */
  updateSyncMessage(message) {
    const syncMessage = document.getElementById('sync-message');
    if (syncMessage) {
      syncMessage.textContent = message;
    }
  }
}

export default new OfflineProgressSync();
